import { FC, Fragment, useState } from 'react';
import moment from 'moment';
// Hooks
import useToggle from 'hooks/useToggle';
// Icons
import { FaTrash, FaRedo, FaRegUser, FaUsers, FaRegClock, FaSignInAlt, FaHistory } from 'react-icons/fa';
import { IoSettings } from "react-icons/io5";
import { LiaToggleOffSolid, LiaToggleOnSolid } from "react-icons/lia";
// Models
import { IUser } from 'models/User';
// Types
import AccountStatuses from 'types/AccountStatuses';
import UserRoles from 'types/UserRoles';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import AccountsAsync from 'store/accounts/accountsAsync';
// Actions
import { singInAs } from 'store/auth/authAsync';
import { resendInvite, deleteUser } from 'store/user/userAsync';
// Selectors
import { selectLoading } from 'store/user/userSelectors';
// Dialogs
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
// Component
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import AccountsFormDialog from 'dialogs/AccountsForm.dialog';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { textFromCamelToNormalCase } from 'utilities/utilities';

type Props = {
  user: IUser;
}

const UsersListItem:FC<Props> = ({
  // Props
  user
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading:boolean = useAppSelector(selectLoading);

  const [ disabled, setDisabled ] = useState(false);

  const { open:dialogConfirmationOpen, toggle:toggleConfirmationDialog } = useToggle();
  const { open:accountsFormDialogOpen, toggle:toggleAccountsFormDialog } = useToggle();
  const { open:openAccountConfirmationDialog, toggle:toggleAccountConfirmationDialog } = useToggle();

  const isDisabled = user.account.status !== AccountStatuses.Active;

  const handleResendUserInvitation = async () => {
    setDisabled(true);
    try {
      await dispatch(resendInvite(user._id)).unwrap();
      setDisabled(false);
    } catch(error){}
  }

  const handleDeleteUser = async () => {
    try {
      await dispatch(deleteUser(user._id)).unwrap();
      toggleConfirmationDialog();
    } catch(error){}
  }

  const handleSignInAs = () => {
    dispatch(singInAs(user._id));
  }

  const handleDisableAccount = () => {
    try {
      if ( isDisabled ) {
        dispatch(AccountsAsync.enableAccount(user.account._id));
      } else {
        dispatch(AccountsAsync.disableAccount(user.account._id));
      }
    } catch(error){}
  }

  const isOwner = user.groups.includes(UserRoles.Owner);

  const getColorByStatus:Record<AccountStatuses, string> = {
    [AccountStatuses.Active]: 'text-green-500',
    [AccountStatuses.Expired]: 'text-yellow-600',
    [AccountStatuses.Deactivated]: 'text-red-500',
  }

  return (
    <Fragment>
      <tr className={`${isDisabled ? 'opacity-75' : ''}`}>
        <td className="text-left title">
          {user.name}
          <span className="lg:hidden">&nbsp;{user.surname}</span>
        </td>
        <td className="text-left hidden lg:table-cell">
          {user.surname}
          {user.passwordRecoveryToken ? (
            <div className="text-xs text-blue-400">{t('pages.users.invitationPending')}</div>
          ) : null}
        </td>
        <td className="text-left text">
          <FaRegUser className="icon" size="16px" />
          {user.email}
        </td>
        <td className="text-left text">
          <FaRegUser className="icon" size="16px" />
          {user.groups.join(', ')}
        </td>
        <td className="text-left text">
          <FaUsers className="icon" size="16px" />
          {user.account?.organization || '-'}
        </td>
        <td className="text-left text">
          {isOwner ? (
            <Fragment>
              <FaRegClock className="icon" size="16px" />
              {user.account.licenseExpirationDate ? moment(user.account.licenseExpirationDate).format('MMM Do YYYY') : t('labels.notSet')}
              <span className={`block ml-1 lg:ml-0 text-xs ${getColorByStatus[user.account.status]}`}>
                {textFromCamelToNormalCase(user.account.status)}
              </span>
            </Fragment>
          ) : null}
        </td>
        <td className="text-left text">
          <FaHistory className="icon" size="16px" />
          {user.lastLogged ? moment(user.lastLogged).format('MMM Do YYYY') : '-'}
        </td>
        <td className="text-right actions">
          {user.passwordRecoveryToken ? (
            <Tooltip title={t('pages.users.resendInvitationButton')} className="ml-2">
              <IconButton
                disabled={disabled}
                onClick={handleResendUserInvitation}
              ><FaRedo /></IconButton>
            </Tooltip>
          ) : null}
          {isOwner ? (
            <Tooltip className="ml-1" title={t('pages.users.signInButton')}>
              <IconButton
                onClick={handleSignInAs}
              ><FaSignInAlt /></IconButton>
            </Tooltip>
          ) : null}
          {isOwner ? (
            <Tooltip className="ml-1" title={t(`pages.users.${isDisabled ? 'enableButton' : 'disableButton'}`)}> 
              <IconButton
                onClick={toggleAccountConfirmationDialog}
              >
                {isDisabled ? <LiaToggleOffSolid /> : <LiaToggleOnSolid />}
              </IconButton>
            </Tooltip>
          ) : null}
          {user.account ? (
            <Tooltip title={t('pages.users.accountSettingsButton')}>
              <IconButton
                onClick={toggleAccountsFormDialog}
              ><IoSettings /></IconButton>
            </Tooltip>
          ) : null}
          <Tooltip className="ml-1" title={t('pages.users.deleteUserButton')}>
            <IconButton
              onClick={toggleConfirmationDialog}
            ><FaTrash /></IconButton>
          </Tooltip>
        </td>
      </tr>
      {user.account && accountsFormDialogOpen ? (
        <AccountsFormDialog accountId={user.account._id} open={accountsFormDialogOpen} onClose={toggleAccountsFormDialog} isOwner={isOwner} />
      ): null}
      {dialogConfirmationOpen ? (
        <ConfirmationDialog
          title={t('pages.users.deleteTitle', { userName: user.name })}
          content={isOwner ? t('pages.users.deleteOwnerConfirmation', { userName: user.name }) : t('pages.users.deleteConfirmation')}
          open={dialogConfirmationOpen}
          loading={loading}
          onClose={toggleConfirmationDialog}
          onConfirm={handleDeleteUser}
        />
      ) : null}
      {openAccountConfirmationDialog ? (
        <ConfirmationDialog
          title={t(`pages.users.${isDisabled ? 'enableTitle' : 'disableTitle'}`, { accountName: user.account.organization })}
          content={t(`pages.users.${isDisabled ? 'enableConfirmation' : 'disableConfirmation'}`)}
          open={openAccountConfirmationDialog}
          loading={loading}
          onClose={toggleAccountConfirmationDialog}
          onConfirm={handleDisableAccount}
        />
      ) : null}
    </Fragment>
  )
}

export default UsersListItem;
